import React, { useState, useEffect, useRef } from "react";
import "react-toastify/dist/ReactToastify.css";
import { dsToast } from "../../utils/dsToast";
import Swal from "sweetalert2";
import ApiCall from "../../../Apicall/ApiCall";
import { useSignalR } from "../../../context/SignalRContext.js";
import { useUser } from "../../../context/UserContext.js";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import BankIdSignModal from "../../../components/BankIdSignModal";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import SellerFlowTrack from "./SellerFlowTrack";
import { BackArrow, ForwardArrow } from "./StatusMark";
import BuyerReviewSections from "./BuyerReviewSections";
import { confirmLeaveDeal } from "./dealDialogs";
import { guardedNavigate } from "./DealSessionManager";
import { saveDealStep } from "../../lib/dealStep";
import { normalizeRegistration } from "../../lib/registrationNo";
import { BANKID_MOCK_SIGN, mockSignRefPrepared } from "../../lib/mockBankId";
import { logDealEvent } from "../../../utils/dealLog";
import useSellerPriceLock from "./useSellerPriceLock";
import PriceLockOverlay from "./PriceLockOverlay";
import { readLockedField } from "./contractUiRealtime";
import VehicleTypeVisual from "../vehicles/VehicleTypeVisual";
import { visualKindFor, vehicleIconFor, vehicleLabelFor, getUserProfile } from "./SellerProfileFlow";
import { formatRegNumber } from "../../lib/format.jsx";
import RegistrationPlate from "../common/RegistrationPlate";
import { useBankIdVerification } from "../auth/BankIdVerification";

const firstName = (name) => (name || "").split(" ")[0] || name;

// Buyer journey milestones on the shared ds-track rail. Same rule as the
// seller: the vehicle-type step itself carries no rail; it appears from the
// registration step onward. Steps 2 (connecting) and 3 (waiting for the
// seller's valuation) both sit on the "connection" milestone.
const BUYER_MILESTONES = [
  { key: "vehicle", atStep: 0 },
  { key: "registration", atStep: 1 },
  { key: "connection", atStep: 2 },
  { key: "contract", atStep: 4 },
];


function BuyerProfile() {
  // Joining a deal needs a BankID-verified buyer: an e-mail sign-up verifies first, then joins.
  const { ensureVerified, verificationDialogs } = useBankIdVerification();
  const {
    connectToDeal,
    messages,
    connection,
    sendDealMessage,
    leaveDealGroup,
  } = useSignalR();
  const { t } = useTranslation();
  const { userId, displayName } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // -------------------Maneging States-------------------------------
  // A caller (e.g. the QR landing's "not the right deal") can open straight on the
  // registration-number step via navigation state. The QR invite landing
  // (BuyerInviteFlow) already knows the contractId — no reg-number lookup
  // needed — so it opens straight on step 2 (the same "waiting for the
  // seller to confirm" screen the reg-number flow lands on) with that id.
  const [activeStep, setActiveStep] = useState(location.state?.startStep ?? 0);
  const [vehicleData, setVehicleData] = useState([]);
  const [error, setError] = useState(null);
  const [valuationToastShown, setValuationToastShown] = useState(false);
  const [sellerValuation, setSellerValuation] = useState(null);
  const [contractMainData, setContractMainData] = useState(null);
  const [contractMainData3, setContractMainData3] = useState(null);
  const [registration, setRegistration] = useState("");
  const [regError, setRegError] = useState("");
  const regInputRef = useRef(null);
  const [contractId, setContractId] = useState(location.state?.inviteContractId ?? null);
  // Remember the buyer's current step on the contract (resume point after re-login).
  useEffect(() => {
    if (contractId && activeStep >= 2) saveDealStep(contractId, activeStep);
  }, [activeStep, contractId]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [oldValuation, setOldValuation] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [disconnectNotice, setDisconnectNotice] = useState(!!location.state?.disconnected);
  const [noSale, setNoSale] = useState(false); // UI only: car lookup failed -> show the "no active sale" note
  // Matches the mock: "Not yet signed" is the hard-coded default for both
  // parties on every load — signing state is never read from the contract
  // record. It only ever changes within this session: this party's own
  // BankID success sets its own moment directly; the other party's arrives
  // solely via the "(Seller|Buyer) signed" SignalR notice.
  const [buyerSignedAt, setBuyerSignedAt] = useState(null);
  const [sellerSignedAt, setSellerSignedAt] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const sellerSignedHandledRef = useRef(false);
  const bothSignedNavigateRef = useRef(false);

  // Entering directly on step 2 from the QR invite: the reg-number flow's step 1
  // submit does `await connectToDeal(validId)` before moving to step 2 — this is
  // that same call, just triggered on mount instead of a form submit, since the
  // invite landing already resolved the contractId from the QR itself.
  const inviteConnectedRef = useRef(false);
  useEffect(() => {
    if (![2, 4].includes(location.state?.startStep) || !contractId) return;
    if (inviteConnectedRef.current) return;
    inviteConnectedRef.current = true;
    connectToDeal(contractId).catch((err) => {
      console.error("Error connecting via SignalR:", err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchContractMain = async () => {
      if (activeStep === 4 && contractId) {
        setLoading(true);
        setError("");

        try {
          const response = await ApiCall({
            url: "/ContractMain/GetContractMainById",
            method: "GET",
            params: { Id: contractId },
          });

          const data = response.data?.result;

          if (!data) {
            const backendError =
              response?.error?.details ||
              response?.error?.message ||
              "❌ Contract not found.";
            setError(backendError);
            return;
          }

          setContractMainData(data);
          // Resumed straight onto the contract step (re-login): there is no live
          // "Seller added valuation" message to wait for, so take the price from the record.
          if (location.state?.startStep === 4) {
            setSellerValuation(data.carValuationBySeller);
            setOldValuation(data.carValuationBySeller);
            setValuationToastShown(true);
          }
          // Clause 2 (Parties) shows each party's personnummer via their
          // profile — the buyer's page never fetched it before now.
          const [seller, buyer] = await Promise.all([
            getUserProfile(data.sellerUserId),
            getUserProfile(data.buyerUserId || userId),
          ]);
          if (seller) setSellerProfile(seller);
          if (buyer) setBuyerProfile(buyer);
          // console.log(data);
        } catch (err) {
          console.error("Error fetching contract:", err);
          setError("⚠ Something went wrong while fetching contract data.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchContractMain();
  }, [activeStep, contractId]);

  // The live "Seller signed" notice is only a prompt to check: the seller is shown as signed
  // only once the server has recorded their verified BankID signature (sellerSignedAt is
  // set by the backend on a verified signature, never by the client), with the server's time.
  useEffect(() => {
    if (activeStep !== 4 || !contractId) return;
    const signed = messages.find((msg) => msg.user === "seller" && msg.message === "Seller signed");
    if (!signed || sellerSignedHandledRef.current) return;
    (async () => {
      try {
        const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: contractId } });
        const d = res?.result || res?.data?.result;
        if (d?.sellerSignedAt && !sellerSignedHandledRef.current) {
          sellerSignedHandledRef.current = true;
          setSellerSignedAt(d.sellerSignedAt);
        }
      } catch (_) {
        /* the periodic server check below picks it up */
      }
    })();
  }, [messages, activeStep, contractId]);

  // Pending until the other party signs: besides the live "Seller signed" notice, ask the
  // server every few seconds so a missed message can never leave the buyer waiting.
  useEffect(() => {
    if (activeStep !== 4 || !contractId || !buyerSignedAt || sellerSignedAt) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: contractId } });
        const d = res?.result || res?.data?.result;
        if (d?.sellerSignedAt) {
          sellerSignedHandledRef.current = true;
          setSellerSignedAt(d.sellerSignedAt);
        }
      } catch (_) {
        /* keep waiting */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [activeStep, contractId, buyerSignedAt, sellerSignedAt]);

  // Once both parties have signed (this session's own knowledge of it —
  // see above), the signed lines are the receipt — hold them on screen
  // briefly, then move on to the read-only contract page.
  // leaveDealGroup/navigate/contractId are read through this ref rather than
  // as effect dependencies: those two functions are new references on most
  // renders, and depending on them here would re-run the effect on any
  // unrelated re-render in the 1200ms window, clearing the pending timer —
  // which the ref-guard above then refuses to reschedule, silently losing
  // the navigation. The effect itself only cares about the two signed
  // moments actually changing.
  const navRef = useRef({ leaveDealGroup, navigate, contractId });
  navRef.current = { leaveDealGroup, navigate, contractId };
  useEffect(() => {
    if (!sellerSignedAt || !buyerSignedAt) return;
    if (bothSignedNavigateRef.current) return;
    bothSignedNavigateRef.current = true;
    const timer = setTimeout(async () => {
      const { leaveDealGroup, navigate, contractId } = navRef.current;
      if (leaveDealGroup) await leaveDealGroup();
      // Straight to the payment stage, not the agreement: the buyer pays next.
      navigate(`/Payment/${contractId}`);
    }, 1200);
    return () => clearTimeout(timer);
  }, [sellerSignedAt, buyerSignedAt]);

  const handleSearch = async () => {
    if (!registration) return;

    setLoading(true);
    setError("");
    setNoSale(false);
    setContractId(null);

    try {
      const carResponse = await ApiCall({
        url: "/CarInfo/GetActiveCarForBuyerByRegNo",
        method: "GET",
        // Canonical form, so the seller's car is found however the buyer typed the plate.
        params: { Id: normalizeRegistration(registration) },
      });

      if (carResponse.data?.result) {
        const raw = carResponse.data.result;
        const contractId = raw.contractId;

        setContractId(contractId || null);

        try {
          const expiryResponse = await ApiCall({
            url: "/ContractMain/CheckDealExpiry",
            method: "POST",
            data: { id: contractId },
          });

          if (expiryResponse.data?.error) {
            const expiryError =
              expiryResponse.data.error.details ||
              expiryResponse.data.error.message ||
              "⚠️ Deal expiry check failed.";
            setError(expiryError);
            return;
          }

          // UI only: hand the validated id back so the single Connect button can proceed.
          return contractId;
          // toast.success("Car and contract are valid!", {
          //   position: "bottom-right",
          // });
        } catch (expiryErr) {
          console.error("Error checking deal expiry:", expiryErr);

          if (expiryErr.response?.data?.error) {
            const expiryError =
              expiryErr.response.data.error.details ||
              expiryErr.response.data.error.message ||
              "⚠️ Deal expiry check failed.";
            setError(expiryError);
          } else {
            setError("⚠️ Something went wrong while checking deal expiry.");
          }
          return;
        }
      } else if (
        carResponse.data?.success === false &&
        carResponse.data?.error
      ) {
        const backendError =
          carResponse.data.error.details ||
          carResponse.data.error.message ||
          "⚠️ Internal server error occurred.";
        setError(backendError);
        setNoSale(true);
      } else {
        setError("❌ Car not found, please check the registration number.");
        setNoSale(true);
      }
    } catch (error) {
      console.error("Error fetching car info:", error);

      // Already in another open deal: a clear popup — not the "no active sale" note.
      if (
        ["Active deal in progress", "Deal already connected"].includes(error.response?.data?.error?.message)
      ) {
        setError("");
        setNoSale(false);
        Swal.fire({
          icon: "warning",
          title: error.response.data.error.message,
          text: error.response.data.error.details,
        });
        return;
      }

      if (error.response?.data?.error) {
        const backendError =
          error.response.data.error.details ||
          error.response.data.error.message ||
          "⚠️ Something went wrong while fetching car info.";
        setError(backendError);
        setNoSale(true);
      } else {
        setError("⚠️ Something went wrong while fetching car info.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchContract = async () => {
      if (activeStep !== 2) return;
      if (!contractId) return;

      setLoading(true);
      setError("");

      try {
        const response = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractId },
        });

        const data = response.data?.result;

        if (!data) {
          setError("❌ Contract not found.");
          return;
        }

        setContractMainData3(data);
      } catch (err) {
        console.error(err);
        setError("⚠ Something went wrong while fetching contract data.");
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [activeStep, contractId]);

  useEffect(() => {
    if (activeStep !== 2) return;
    if (!contractMainData3) return;
    if (updated || submitting) return;

    const timer = setTimeout(() => {
      handleBuyerConnect();
    }, 3000);

    return () => clearTimeout(timer);
  }, [activeStep, contractMainData3, updated, submitting]);

  const handleBuyerConnect = async () => {
    if (!contractMainData3) {
      Swal.fire("❌ Error", "No contract info available", "error");
      return;
    }
    if (!(await ensureVerified())) return;

    setSubmitting(true);

    try {
      const payload = {
        sellerUserId: contractMainData3.sellerUserId,
        sellerUserFullName: contractMainData3.sellerUserFullName,
        sellerDealStatus: contractMainData3.sellerDealStatus,
        sellerDealActive: contractMainData3.sellerDealActive,
        sellerDealComplete: contractMainData3.sellerDealComplete,
        vahicleTypeOptionId: contractMainData3.vahicleTypeOptionId,
        vahicleTypeOptionVahicleTypeName:
          contractMainData3.vahicleTypeOptionVahicleTypeName,
        vahicleTypeOptionName: contractMainData3.vahicleTypeOptionName,
        sellerDrivingLicensePath: "",
        sellerDrivingLicenseValidate:
          contractMainData3.sellerDrivingLicenseValidate,

        carInfoId: contractMainData3.carInfoId,
        carInfoRegistrationNo: contractMainData3.carInfoRegistrationNo,
        carInfoVehicleDesignation: contractMainData3.carInfoVehicleDesignation,
        carInfoTradeName: contractMainData3.carInfoTradeName,
        carInfoYearModel: contractMainData3.carInfoYearModel,
        carInfoVehicleYears: contractMainData3.carInfoVehicleYears,
        carInfoRegistrationDate: contractMainData3.carInfoRegistrationDate,
        carInfoNumberOfUsers: contractMainData3.carInfoNumberOfUsers,
        carInfoWaxelbarge: contractMainData3.carInfoWaxelbarge,
        carInfoFourWheelDrive: contractMainData3.carInfoFourWheelDrive,
        carInfoFuel: contractMainData3.carInfoFuel,
        carInfoElectricVehicleConfiguration:
          contractMainData3.carInfoElectricVehicleConfiguration,
        carInfoInspectionDate: contractMainData3.carInfoInspectionDate,
        carInfoInspectionDateEMPTY:
          contractMainData3.carInfoInspectionDateEMPTY,
        carInfoDispensationInspectionGroup:
          contractMainData3.carInfoDispensationInspectionGroup,
        carInfoInspectionGroup: contractMainData3.carInfoInspectionGroup,
        carInfoFeedingStall: contractMainData3.carInfoFeedingStall,
        carInfoInspectionStation: contractMainData3.carInfoInspectionStation,
        carInfoInspectionProgramCode:
          contractMainData3.carInfoInspectionProgramCode,
        carInfoPreviousInspectionDate:
          contractMainData3.carInfoPreviousInspectionDate,
        carInfoPreviousInspectionProgramCode:
          contractMainData3.carInfoPreviousInspectionProgramCode,
        carInfoTax: contractMainData3.carInfoTax,
        carInfoMalus: contractMainData3.carInfoMalus,
        carValuationBySeller: contractMainData3.carValuationBySeller,

        buyerUserId: userId,
        buyerDealStatus: contractMainData3.buyerDealStatus,
        buyerDealConnected: true,
        buyerDealConfirmed: contractMainData3.buyerDealConfirmed,

        lastModificationTime: contractMainData3.lastModificationTime,
        lastModifierUserId: contractMainData3.lastModifierUserId,
        creationTime: contractMainData3.creationTime,
        creatorUserId: contractMainData3.creatorUserId,
        id: contractMainData3.id,
      };

      const contractUpdateRes = await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: payload,
      });

      if (contractUpdateRes?.success === false || contractUpdateRes?.error) {
        const errorMsg =
          contractUpdateRes?.error?.details ||
          contractUpdateRes?.error?.message ||
          "Failed to update contract";
        Swal.fire("❌ Error", errorMsg, "error");
        return;
      }

      if (!connection) {
        await connectToDeal(contractMainData3.id);
      }

      await sendDealMessage("buyer", "Buyer connected to Seller");

      // deal log: this buyer reached the deal through the seller's QR code
      if (location.state?.inviteContractId) logDealEvent(contractMainData3.id, "qr_scanned");

      setUpdated(true);

      // The buyer stays here — this only announces they've joined. Moving on
      // to step 3 now waits for the seller's own "Seller confirmed buyer"
      // message (see the effect below); it used to advance on a fixed local
      // timer regardless of whether the seller had confirmed anything.
    } catch (err) {
      console.error(err);
      const backendError = err?.response?.data?.error;
      Swal.fire("❌ Error", backendError?.details || backendError?.message || "Something went wrong!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Only the seller's own "Seller confirmed buyer" message moves the buyer
  // on from step 2 — matching the reference (the buyer's waiting screen is
  // passive; only the seller's confirmation changes it).
  useEffect(() => {
    if (activeStep !== 2 || !updated) return;
    const confirmed = messages.find(
      (msg) => msg.user === "seller" && msg.message === "Seller confirmed buyer",
    );
    if (!confirmed) return;
    setActiveStep((prev) => prev + 1);
  }, [messages, activeStep, updated]);

  useEffect(() => {
    const fetchVehicleOptions = async () => {
      try {
        setLoading(true);
        const response = await ApiCall({
          url: "/VahicleTypeOption/GetAll",
          method: "GET",
        });

        if (response?.data?.result?.items) {
          // Klargo currently supports only car, motorcycle and moped/scooter.
          // Hide any other vehicle types (boat, trailer, truck, camper, …) even
          // if the backend still returns them.
          const ALLOWED_VEHICLE_TYPES = ["car", "motorcycle", "scooter", "moped", "mc"];
          setVehicleData(
            response.data.result.items.filter((v) =>
              ALLOWED_VEHICLE_TYPES.includes(
                String(v?.vahicleTypeName || "").trim().toLowerCase()
              )
            )
          );
        } else {
          setVehicleData([]);
        }
      } catch (err) {
        setError(err.message || "API call failed");
      } finally {
        setLoading(false);
      }
    };

    if (activeStep === 0) {
      fetchVehicleOptions();
    }
  }, [activeStep]);

  // useEffect(() => {
  //   if (activeStep === 3) {
  //     const dealMsg = messages.find(
  //       (msg) =>
  //         msg.message === "Buyer connected! Valuation entered" ||
  //         msg.message === "Seller added valuation"
  //     );

  //     if (dealMsg && !valuationToastShown) {
  //       toast.success("💰 Seller has added car valuation!", {
  //         position: "top-right",
  //       });
  //       setValuationToastShown(true);
  //       setSellerValuation(true);
  //     }
  //   }
  // }, [messages, activeStep, valuationToastShown]);

  useEffect(() => {
    if (activeStep === 3 && !valuationToastShown) {
      const dealMsg = messages.find(
        (msg) =>
          msg.message
            ?.toLowerCase()
            .includes("Buyer connected! Valuation entered") ||
          msg.message?.toLowerCase().includes("seller added valuation"),
      );

      if (dealMsg) {
        dsToast("success", {
          title: t("buyer.sellerValue"),
          text: t("buyer.sellerValuationToastText"),
        }, { position: "bottom-right" });
        setValuationToastShown(true);
        setSellerValuation(true);
      }
    }
  }, [messages, activeStep, valuationToastShown]);

  // Matches the reference: the seller's own confirmation is the only trigger,
  // so the buyer moves on as soon as the seller's valuation/prep arrives —
  // no separate buyer-side click to wait for.
  useEffect(() => {
    let timer;

    if (activeStep === 3 && sellerValuation) {
      timer = setTimeout(() => setActiveStep(4), 2000);
    }

    return () => clearTimeout(timer);
  }, [activeStep, sellerValuation]);

  useEffect(() => {
    if (activeStep !== 4 || !contractId) return;

    const valuationMsg = messages.find(
      (msg) =>
        msg.user === "seller" &&
        (msg.message === "Seller added valuation" ||
          msg.message === "Seller Updated valuation"),
    );

    if (!valuationMsg) return;

    const fetchVehicleInfo = async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractId },
        });

        const latest = res?.result || res?.data?.result;
        if (!latest) return;

        const newValuation = latest.carValuationBySeller;

        if (oldValuation !== null && newValuation !== oldValuation) {
          dsToast("info", {
            title: t("buyer.priceUpdatedTitle"),
            text: t("buyer.priceUpdatedText", { old: oldValuation, new: newValuation }),
          }, { position: "bottom-right" });
        }
        setOldValuation(newValuation);
        setContractMainData(latest);
        setSellerValuation(newValuation);
        setValuationToastShown(true);
      } catch (err) {
        console.error("Error fetching vehicle info:", err);
      }
    };

    fetchVehicleInfo();
  }, [messages, activeStep, contractId, oldValuation]);

  const handleDealConfirm = async () => {
    // deal log: the buyer confirmed the price and terms
    logDealEvent(contractId, "price_confirmed");
    // deal log: the buyer pressed the BankID signing button
    logDealEvent(contractId, "bankid_started");
    // DEV/TEST: mock signing (no BankID popup). Real BankID flow is unchanged when unset.
    // Never fall back to the real popup while mocking — a missing contract id
    // is surfaced as an error by executeConfirm instead.
    if (BANKID_MOCK_SIGN) {
      executeConfirm(contractId ? await mockSignRefPrepared("buyer", contractId) : null);
      return;
    }
    setSignModalOpen(true);
  };

  const executeConfirm = async (orderRef) => {
    if (!contractId) {
      Swal.fire("Error", "Contract ID not found", "error");
      return;
    }
    if (!orderRef) {
      Swal.fire("Error", "BankID signing reference missing", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await ApiCall({
        url: "/ContractMain/DealCompletedByBuyer",
        method: "POST",
        data: { id: contractId, orderRef },
      });

      if (res?.data?.success || res?.success) {
        // Matches the mock: no success popup and no leaving the page for one
        // party's own signature — the status line updates in place, and the
        // buyer only moves on once BOTH parties have signed (see the
        // "both signed" effect above).
        setBuyerSignedAt(new Date().toISOString());
        try {
          await sendDealMessage("buyer", "Buyer signed");
        } catch (_) {
          // best-effort SignalR notice to the seller
        }
      } else {
        Swal.fire(
          "Failed",
          res?.data?.error?.message || "Deal confirmation failed",
          "error",
        );
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.error?.message || "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };


  // "This is not the right deal": same fetch-merge-PUT on ContractMain/Update the
  // seller's "wrong buyer" uses (clears the buyer link), then leave the SignalR group.
  // Seller "Change price": lock while their dialog is open, reload the price once saved.
  const refreshPrice = async () => {
    if (!contractId) return;
    try {
      const res = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: contractId },
      });
      const latest = res?.result || res?.data?.result;
      if (!latest) return;
      setContractMainData3(latest);
      setContractMainData((prev) => (prev ? latest : prev));
    } catch (err) {
      console.error("Error refreshing price:", err);
    }
  };
  const priceLocked = useSellerPriceLock(messages, refreshPrice);
  // the seller is editing the odometer reading / number of keys
  const lockedField = readLockedField(messages);

  // Back to the registration-number (car search) step with all deal state cleared.
  const resetToSearch = async (notify) => {
    if (leaveDealGroup) {
      try {
        await leaveDealGroup();
      } catch (err) {
        console.error("Failed to leave SignalR deal group:", err);
      }
    }
    messages.length = 0;
    setSellerValuation(null);
    setValuationToastShown(false);
    setContractMainData3(null);
    setContractMainData(null);
    setContractId(null);
    setUpdated(false);
    setSubmitting(false);
    setError("");
    setNoSale(false);
    setRegistration("");
    setDisconnectNotice(!!notify);
    setActiveStep(1);
  };

  // The seller pressed "Wrong buyer": the contract's buyer link is already cleared
  // on their side. Drop out of the deal and land on the car search with a notice.
  useEffect(() => {
    if (activeStep < 2) return;
    const kicked = messages.find(
      (msg) => msg.user === "seller" && msg.message === "Buyer disconnected by seller",
    );
    if (!kicked) return;
    resetToSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, activeStep]);

  // The actual disconnect mutation, split out of handleWrongDeal so the
  // session-timeout "wait or disconnect" dialog below can run it directly —
  // that dialog is already the confirmation, no second nested prompt needed.
  const disconnectFromDealNow = async () => {
    try {
      // The buyer's own leave has its own endpoint: the server removes the buyer link entirely, so
      // the deal leaves this buyer's transaction history instead of staying as a disconnected row.
      await ApiCall({
        url: "/ContractMain/DisconnectSelfAsBuyer",
        method: "POST",
        data: { id: contractId },
      });

      // The server tells the seller (their screen restores its waiting/QR stage) once the link is
      // gone — a message sent from here would be refused, the buyer no longer being a party.
      await resetToSearch(false);
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to leave the deal", "error");
    }
  };

  const handleWrongDeal = async () => {
    if (!(await confirmLeaveDeal(t))) return;
    await disconnectFromDealNow();
  };

  const backToOverview = () => guardedNavigate("/user/ChooseAction");

  return (
    <PublicPage>
      {verificationDialogs}
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          {priceLocked && activeStep >= 2 && <PriceLockOverlay t={t} />}
          {!priceLocked && lockedField && activeStep >= 2 && <PriceLockOverlay t={t} field={lockedField} />}
          {activeStep >= 1 && (
            <div className="flow-shell-track">
              <SellerFlowTrack activeStep={activeStep} t={t} milestones={BUYER_MILESTONES} ns="buyer" />
            </div>
          )}

          {/* ------------------ STEP 0 — choose vehicle type ------------------ */}
          {activeStep === 0 && (
            <div className="mx-auto w-full max-w-5xl">
              <h1 className="ds-title-step mb-8 text-center">{t("seller.chooseVehicleTitle")}</h1>

              {loading ? (
                <div className="flex justify-center py-12" role="status" aria-label={t("seller.fetchingCarDetails")}>
                  <div
                    className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
                    style={{ borderTopColor: "var(--ds-green)" }}
                  />
                </div>
              ) : error ? (
                <p className="ds-body py-6 text-center font-semibold text-red-600">{error}</p>
              ) : vehicleData.length === 0 ? (
                <p className="ds-body py-6 text-center font-semibold">{t("seller.noVehicleTypes")}</p>
              ) : (
                <div key="vehicle-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-3 motion-safe:animate-fade-in">
                  {vehicleData.map((veh) => {
                    const kind = visualKindFor(veh.vahicleTypeName);
                    return (
                      <button
                        key={veh.id}
                        type="button"
                        onClick={() => setActiveStep(1)}
                        className="ds-object-press group flex flex-col overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet text-left shadow-[0_1px_2px_rgba(16,30,40,.05)]"
                      >
                        {kind ? (
                          <span className="flex w-full items-end justify-center px-8 pb-1 pt-7" aria-hidden="true">
                            <VehicleTypeVisual kind={kind} className="max-w-[220px]" />
                          </span>
                        ) : (
                          <span className="flex w-full items-center justify-center py-7" aria-hidden="true">
                            <span
                              className="flex h-14 w-14 items-center justify-center rounded-full"
                              style={{ background: "var(--ds-green-bg)" }}
                            >
                              {vehicleIconFor(veh.vahicleTypeName)}
                            </span>
                          </span>
                        )}
                        <span className="flex min-h-14 items-center justify-between border-t border-ds-rule-row px-5 py-4">
                          <span className="font-ds-serif text-lg font-semibold text-ds-ink">{vehicleLabelFor(veh.vahicleTypeName, t)}</span>
                          <span
                            className="flex items-center gap-2 font-ds-sans text-sm font-medium text-ds-text transition-colors group-hover:text-ds-ink"
                            aria-hidden="true"
                          >
                            {t("seller.selectCta")} <ForwardArrow stroke="currentColor" width={15} height={11} />
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                className="mx-auto mt-5 flex max-w-xl flex-col justify-center gap-2 sm:flex-row"
                aria-label={t("nav.comingSoon")}
              >
                {[
                  { kind: "camper", label: t("seller.futureVehicles.camper") },
                  { kind: "lightTruck", label: t("seller.futureVehicles.lightTruck") },
                ].map((fv) => (
                  <div
                    key={fv.kind}
                    className="flex min-w-0 items-center gap-3 rounded-[9px] border border-ds-rule-row bg-ds-sheet px-4 py-2.5 text-ds-muted opacity-60"
                  >
                    <VehicleTypeVisual kind={fv.kind} className="w-[76px] flex-none sm:w-[84px]" />
                    <span className="min-w-0">
                      <span className="block font-ds-sans text-sm font-semibold text-ds-text">{fv.label}</span>
                      <span className="block font-ds-sans text-xs text-ds-muted">{t("nav.comingSoon")}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-center">
                <button type="button" onClick={backToOverview} className="ds-action-text py-[15px]">
                  <BackArrow />
                  <span className="ds-underline">{t("seller.backToOverview")}</span>
                </button>
              </div>
            </div>
          )}

          {/* ------------------ STEP 1 — registration number ------------------ */}
          {/* Restyled to the reference's join entry (Buyer.tsx): a compact
              back link, a serif step title, an unplaceholdered mono reg field
              and an ink button sized to its own content — the same grammar
              the seller's create-deal step already uses. All state and
              handlers below (handleSearch/connectToDeal/setActiveStep,
              error/noSale, disconnectNotice) are unchanged. */}
          {activeStep === 1 && (
            <div className="ds-form mx-auto w-full max-w-2xl">
              <div className="mb-3 -mt-2">
                <button type="button" onClick={() => setActiveStep(0)} className="ds-action-text">
                  <BackArrow />
                  <span>{t("seller.backToOverview")}</span>
                </button>
              </div>

              <div className="flex flex-col gap-5">
                {disconnectNotice && (
                  <div role="status" className="ds-panel-note">
                    <h2 className="text-sm font-semibold text-ds-ink">{t("buyer.disconnected.title")}</h2>
                    <p className="mt-1 text-sm leading-relaxed text-ds-text">{t("buyer.disconnected.body")}</p>
                  </div>
                )}
                <div>
                  <h1 className="ds-title-step">{t("buyer.startPurchaseTitle")}</h1>
                  <p className="ds-body mt-1.5 text-ds-muted">{t("buyer.startPurchaseSubtitle")}</p>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!registration.trim()) {
                      setRegError(t("seller.error.regRequired"));
                      regInputRef.current?.focus();
                      return;
                    }
                    setRegError("");
                    const validId = await handleSearch();
                    if (!validId) return;
                    try {
                      setLoading(true);
                      await connectToDeal(validId);
                      setActiveStep(2);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  noValidate
                  className="flex flex-col gap-4 sm:max-w-[528px]"
                >
                  <div>
                    <label htmlFor="buyer-reg" className="ds-label block">
                      {t("buyer.regLabel")}
                    </label>
                    <input
                      id="buyer-reg"
                      ref={regInputRef}
                      className="ds-input ds-input--mono mt-1.5 h-11 uppercase text-[17px] font-semibold tracking-[0.06em]"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      value={registration}
                      onChange={(e) => {
                        setRegistration(e.target.value.toUpperCase());
                        if (regError) setRegError("");
                      }}
                      aria-invalid={!!regError}
                      aria-describedby={regError ? "buyer-reg-error" : error ? "buyer-reg-panel" : undefined}
                    />
                    {regError && (
                      <p id="buyer-reg-error" role="alert" className="ds-error-text mt-2">
                        {regError}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div id="buyer-reg-panel" role="alert" className="ds-panel-note">
                      {noSale ? (
                        <>
                          <h2 className="text-sm font-semibold text-ds-ink">
                            {t("buyer.noSale.title", { reg: formatRegNumber(registration) })}
                          </h2>
                          <p className="mt-1 text-sm leading-relaxed text-ds-text">{t("buyer.noSale.body")}</p>
                        </>
                      ) : (
                        <p className="text-sm leading-relaxed text-ds-text">{error}</p>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-7">
                    <button
                      type="submit"
                      disabled={loading}
                      className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px] disabled:opacity-60"
                    >
                      {loading ? <span className="ds-mono text-[13px]">...</span> : t("buyer.connectCta")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ------------------ STEP 2 — waiting for the seller to confirm ------------------ */}
          {/* Restyled to the reference's buyer "waiting" surface
              (DealFlowConnected.tsx, joined && !isSeller): the deal band
              (plate + vehicle type — no price, that's seller-only) so the
              buyer can see what car they joined, a heading naming the
              seller, and a quiet way to leave. This is a genuine wait, not a
              flash: `updated` only means the join itself was announced
              (ContractMain/Update + the "Buyer connected to Seller"
              message) — the buyer stays on this screen until the effect
              above sees the seller's own "Seller confirmed buyer" message
              and advances. It used to leave on a fixed 1.5s local timer
              regardless of whether the seller had done anything. */}
          {activeStep === 2 && (
            <div className="ds-form mx-auto w-full max-w-2xl">
              {contractMainData3 && (
                <div className="mb-8 flex flex-wrap items-center gap-4 border-t-2 border-ds-ink pt-4" data-testid="deal-band">
                  {contractMainData3.carInfoRegistrationNo && (
                    <RegistrationPlate value={contractMainData3.carInfoRegistrationNo} size="row" />
                  )}
                  {contractMainData3.vahicleTypeOptionName && (
                    <span className="text-[15px] font-semibold text-ds-ink">
                      {contractMainData3.vahicleTypeOptionName}
                    </span>
                  )}
                </div>
              )}

              <h1 className="m-0 text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.12] text-ds-ink">
                {contractMainData3
                  ? t("buyer.connectSellerTitle", { name: contractMainData3.sellerUserFullName || "" })
                  : t("buyer.connectingWait")}
              </h1>

              {contractMainData3 && (
                <p role="status" aria-live="polite" className="ds-body mt-3 max-w-[54ch] text-ds-text">
                  {t("buyer.autoUpdate", { first: firstName(contractMainData3.sellerUserFullName) })}
                </p>
              )}

              {!contractMainData3 && (
                <div className="mt-8 flex items-center gap-3" role="status" aria-label={t("buyer.connectingWait")}>
                  <div
                    className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
                    style={{ borderTopColor: "var(--ds-green)" }}
                  />
                </div>
              )}

              {contractMainData3 && (
                <div className="mt-8 border-t border-ds-rule-row pt-2">
                  <button type="button" onClick={handleWrongDeal} className="ds-action-text">
                    <span className="ds-underline">{t("buyer.leaveDeal")}</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ------------------ STEP 3 — preparing the contract ------------------ */}
          {/* Matches the reference's confirmBuyer(): the seller's own click is
              the only trigger for both sides, so by the time the buyer reaches
              this step (only ever entered after "Seller confirmed buyer") the
              seller has already started the same fetch-merge-PUT this screen is
              waiting on. There is no buyer-side "Start the deal" click — the
              buyer only ever watches this happen, exactly like DealFlowConnected
              shows the identical "Förbereder kontraktet" surface to both roles
              off one shared write. All state/handlers (sellerValuation/
              handleWrongDeal) are unchanged. */}
          {activeStep === 3 && (
            <div className="ds-form mx-auto w-full max-w-2xl">
              {contractMainData3 && (
                <div className="mb-8 flex flex-wrap items-center gap-4 border-t-2 border-ds-ink pt-4" data-testid="deal-band">
                  {contractMainData3.carInfoRegistrationNo && (
                    <RegistrationPlate value={contractMainData3.carInfoRegistrationNo} size="row" />
                  )}
                  {contractMainData3.vahicleTypeOptionName && (
                    <span className="text-[15px] font-semibold text-ds-ink">
                      {contractMainData3.vahicleTypeOptionName}
                    </span>
                  )}
                </div>
              )}

              <h1 className="m-0 text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.12] text-ds-ink">
                {t("agreement.retrievingTitle")}
              </h1>

              <p
                role="status"
                aria-live="polite"
                className="mt-3 flex max-w-[54ch] items-start gap-3 text-[15px] leading-[1.55] text-ds-text"
              >
                <span
                  className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-ds-green motion-safe:animate-pulse"
                  aria-hidden="true"
                />
                <span>{t("agreement.retrievingBody")}</span>
              </p>
            </div>
          )}

          {/* ------------------ STEP 4 — review & confirm ------------------ */}
          {activeStep === 4 && (
            <BuyerReviewSections t={t} contractMainData={contractMainData} buyerName={displayName} loading={loading} onConfirm={handleDealConfirm} messages={messages} signedAt={{ seller: sellerSignedAt, buyer: buyerSignedAt }} signing={signModalOpen || loading} onCancelSigning={() => setSignModalOpen(false)} sellerProfile={sellerProfile} buyerProfile={buyerProfile} />
          )}

          <BankIdSignModal
            presentation="contract"
            signData={{
              contractId: contractId,
              role: "buyer",
              reg: contractMainData?.carInfoRegistrationNo || registration,
              carName: [
                contractMainData?.carInfoVehicleDesignation,
                contractMainData?.carInfoTradeName,
              ].filter(Boolean).join(" "),
              price: contractMainData?.carValuationBySeller,
              sellerName: contractMainData?.sellerUserFullName,
              buyerName: displayName,
              version:
                contractMainData?.lastModificationTime ||
                contractMainData?.creationTime,
            }}
            open={signModalOpen}
            onClose={() => setSignModalOpen(false)}
            onSuccess={async (_pn, orderRef) => {
              setSignModalOpen(false);
              await executeConfirm(orderRef);
            }}
          />
        </main>
      </div>
    </PublicPage>
  );
}

export default BuyerProfile;
